/**
 * Mirror types for the Data Mapper control-plane services
 * (packages/api/src/services/data-mapper/, plan/2026-10-09_data-mapper-into-hq.md).
 *
 * Hand-maintained copies of the API's class/schema shapes — keep them in step
 * with `data-mapper-profiles.class.ts`, `data-mapper-schemas.class.ts`,
 * `data-mapper-lookup-tables.class.ts` and `data-mapper-mapping-configs.class.ts`.
 * All four services answer `find` with a BARE ARRAY (no `{ data, total }`
 * envelope, no pagination).
 */
import type { ParsedSchema, TransformConfig, TransformType } from "./engine/types";

/** Where a schema / lookup table / mapping config lives. Narrowest first. */
export type DataMapperScope = "profile" | "client" | "global";

/** The display order of the scope groups: the profile's own rows first, as the API sorts them. */
export const DATA_MAPPER_SCOPES: readonly DataMapperScope[] = ["profile", "client", "global"];

/** What a profile's saved UI state says, for the picker. `find` only. */
export interface DataMapperProfileStateSummary {
  lastStep: number;
  savedAt: string | null;
  /** The chosen schema's saved name (null when the schema is gone). */
  schemaName: string | null;
  /** The standalone tool's Data Source table selection; HQ never writes it. */
  selectedTableCount: number;
  mappingConfigName: string | null;
  /**
   * Distinct source tables the current mapping configuration maps from. Null:
   * no configuration chosen, or it is gone.
   */
  mappedTableCount?: number | null;
}

/** The Profiles list's "Mapped tables" cell: what the current mapping configuration reads from. */
export const mappedTablesLabel = (summary: DataMapperProfileStateSummary | null | undefined): string => {
  const n = summary?.mappedTableCount;
  if (n == null) return "—";
  return n === 0 ? "None mapped" : `${n} table${n === 1 ? "" : "s"}`;
};

export interface DataMapperProfile {
  id: string;
  clientId: string;
  clientName: string | null;
  clientCode: string | null;
  /** The client's parent company, when the client is a child company (absent from older APIs). */
  clientParentId?: string | null;
  clientParentName?: string | null;
  name: string;
  slug: string;
  description: string | null;
  titanSource: boolean;
  lastStep: number | null;
  lastAccessedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** On `find` only. Null when nothing is saved. */
  stateSummary?: DataMapperProfileStateSummary | null;
  /** On `get`, `create` and `patch`: the saved UI state itself. Never read by these screens. */
  state?: unknown;
}

export interface DataMapperProfileCreate {
  clientId: string;
  name: string;
  description?: string | null;
  titanSource?: boolean;
}

export interface DataMapperProfilePatch {
  name?: string;
  description?: string | null;
  titanSource?: boolean;
  lastStep?: number;
  state?: unknown;
  /** Stamp `lastAccessedAt` (the standalone tool did this on `set-active`). */
  touch?: boolean;
  /** The `updatedAt` you loaded: a concurrent save is then a 409 `record-changed`. */
  expectUpdatedAt?: string;
}

export interface DataMapperSchemaEntry {
  id: string;
  name: string;
  description: string | null;
  schema: unknown;
  scope: DataMapperScope;
  clientId: string | null;
  profileId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DataMapperLookupTable {
  id: string;
  name: string;
  description: string | null;
  scope: DataMapperScope;
  clientId: string | null;
  profileId: string | null;
  createdAt: string;
  updatedAt: string;
  /** On `find`. */
  entryCount?: number;
  /** On `get`: every entry (the source value is always a string). */
  entries?: Array<{ sourceValue: string; targetValue: unknown }>;
}

/** A saved schema's `schema` value: the parsed TypeBox schema, as the standalone tool saved it. */
export type DataMapperStoredSchema = ParsedSchema;

/**
 * A child id the API keeps on a save: letters, digits, `-` and `_` only
 * (data-mapper-mapping-configs.schema.ts `ChildId`). Anything else is never sent.
 */
export const CHILD_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export interface DataMapperTransformStep {
  id?: string;
  type: TransformType;
  config: TransformConfig;
  /** An MSSQL INT. */
  order: number;
  enabled: boolean;
}

export interface DataMapperFieldMapping {
  id?: string;
  sourceTable: string;
  sourceColumn: string;
  targetField: string;
  isAutoMapped?: boolean;
  confidence?: number | null;
  notes?: string | null;
  transforms?: DataMapperTransformStep[];
}

/**
 * A mapping configuration. `find` answers the summary (no `schema`, no
 * `mappings`; `mappingCount` instead); `get`, `create`, `patch` and `update`
 * answer the whole config. Both land in the same ServiceStore cache, and a
 * `get` merges over a summary, so "loaded in full" is `mappings !== undefined`.
 */
export interface DataMapperMappingConfig {
  id: string;
  name: string;
  description: string | null;
  schemaName: string;
  connectionId: string | null;
  scope: DataMapperScope;
  clientId: string | null;
  profileId: string | null;
  createdAt: string;
  updatedAt: string;
  /** Full config only. */
  schema?: DataMapperStoredSchema | Record<string, unknown>;
  /** Full config only. */
  mappings?: DataMapperFieldMapping[];
  /** Summary only. */
  mappingCount?: number;
  /** Summary only. */
  sourceTables?: string[];
}

/**
 * The selection every scoped call names (plan §2.4: the server keeps no
 * per-tab state, so the browser sends its selection with each call). A
 * `profileId` implies its client; the API refuses a `clientId` that disagrees.
 */
export interface DataMapperSelectionQuery {
  clientId?: string;
  profileId?: string;
}

/**
 * Step names, indexed by `lastStep`. 0–8 are the standalone tool's (a profile
 * it saved may hold any of them); HQ's Target and Import screens are 9 and 10,
 * after them, so a standalone "Preview" or "Export" keeps its own name.
 */
export const DATA_MAPPER_STEP_LABELS: readonly string[] = [
  "Client/Profile",
  "Data Source",
  "Explore",
  "Schema",
  "Browse",
  "Map",
  "Transform",
  "Preview",
  "Export",
  "Target",
  "Import",
];

export const stepLabel = (step: number): string =>
  DATA_MAPPER_STEP_LABELS[step] ?? `Step ${step}`;

/**
 * GUIDs compare case-insensitively: MSSQL returns them upper-case, a browser
 * may hold them lower-case (the API's own `sameId`).
 */
export const sameId = (a: unknown, b: unknown): boolean =>
  a != null && b != null && String(a).toLowerCase() === String(b).toLowerCase();

/** Is this row visible to the selection? The API's `whereVisible`, for filtering the shared cache. */
export const visibleToSelection = (
  row: { scope: DataMapperScope; clientId: string | null; profileId: string | null },
  clientId: string | null,
  profileId: string | null
): boolean => {
  if (row.scope === "global") return true;
  if (row.scope === "client") return clientId != null && sameId(row.clientId, clientId);
  return profileId != null && sameId(row.profileId, profileId);
};

/** The `data.reason` a Data Mapper refusal carries, if any. */
export const refusalReason = (err: unknown): string | null => {
  const reason = (err as { data?: { reason?: unknown } } | null)?.data?.reason;
  return typeof reason === "string" ? reason : null;
};

/** 409 `record-changed`: someone else saved this record after it was loaded. */
export const isRecordChanged = (err: unknown): boolean =>
  (err as { code?: unknown } | null)?.code === 409 && refusalReason(err) === "record-changed";

// ---- Source configs and the data plane -------------------------------------
// Mirrors of data-mapper-source-configs.class.ts and data-plane/types.ts +
// handlers.ts (packages/api/src/services/data-mapper/).

/** Every source type the standalone tool knew; HQ's API reads all five. */
export type DataMapperSourceType = "mssql" | "csv" | "excel" | "json" | "rest-api";

/** The three file formats (data-plane/file-source.ts). */
export type DataMapperFileType = "csv" | "excel" | "json";

export const isFileSourceType = (type: DataMapperSourceType): type is DataMapperFileType =>
  type === "csv" || type === "excel" || type === "json";

export const DATA_MAPPER_SOURCE_TYPES: readonly { type: DataMapperSourceType; label: string }[] = [
  { type: "mssql", label: "SQL Server" },
  { type: "csv", label: "CSV file" },
  { type: "excel", label: "Excel workbook" },
  { type: "json", label: "JSON file" },
  { type: "rest-api", label: "REST API" },
];

/**
 * An MSSQL source as the connect / test calls carry it. The password is NOT a
 * field here: it is added to the call body at the moment of the call and is
 * never held by a store (see DataMapperSourceStore).
 */
export interface DataMapperMssqlForm {
  name: string;
  server: string;
  port: string;
  database: string;
  authType: DataMapperSqlAuthType;
  username: string;
  domain: string;
  encrypt: boolean;
  trustServerCertificate: boolean;
}

/**
 * How a SQL Server source signs in: a SQL login, a Windows domain account with
 * its password (NTLM), or "integrated": Windows login as the account the
 * person's HQ Connector runs as, nothing typed (Windows connector only).
 */
export type DataMapperSqlAuthType = "sql" | "windows" | "integrated";

// ---- The HQ Connector ------------------------------------------------------
// Mirrors packages/api/src/services/data-mapper/data-mapper-connector/data-mapper-connector.ts.

export type DataMapperConnectorPlatform = "windows-x64" | "macos-arm64" | "macos-x64";

export interface DataMapperConnectorDownload {
  platform: DataMapperConnectorPlatform;
  label: string;
  fileName: string;
  bytes: number;
  sha256: string;
}

/** The signed-in person's own HQ Connector, and the connector downloads this HQ serves. */
export interface DataMapperConnectorStatus {
  connected: boolean;
  instanceId: string | null;
  connectedAt: string | null;
  /** What the connector said it is; null when none is connected (or it did not say). */
  client: {
    app: string;
    version: string;
    platform: string;
    arch: string;
    sqlAuth?: DataMapperSqlAuthType[];
  } | null;
  latestVersion: string | null;
  updateAvailable: boolean;
  downloads: DataMapperConnectorDownload[];
}

/**
 * A file source as the connect / test / save calls carry it (the API's
 * file-source.ts options plus the upload it names). `fileId` is the HQ document
 * the upload created; only its uploader (or an administrator) can open it.
 */
export interface DataMapperFileForm {
  name: string;
  fileId: string | null;
  originalName: string | null;
  fileSize: number | null;
  delimiter: string;
  hasHeader: boolean;
  encoding: string;
  /** JSON only: the dot path to the array of rows. */
  rootPath: string;
  /** Excel only: sheet names, comma-separated as typed (empty = every sheet). */
  sheets: string;
  tableName: string;
}

export type DataMapperRestAuthType = "none" | "bearer" | "basic" | "api-key";
export type DataMapperRestPaginationType = "none" | "offset" | "page" | "cursor";

/**
 * A REST source's NON-SECRET settings. The token, password, API-key value,
 * every header and the request body are NOT here: the form keeps them in its
 * own component state and adds them to one call body (DataMapperRestSecrets).
 */
export interface DataMapperRestForm {
  name: string;
  url: string;
  method: "GET" | "POST";
  authType: DataMapperRestAuthType;
  /** Basic auth's user name. */
  username: string;
  /** The API-key header's NAME (its value is a secret). */
  apiKeyHeader: string;
  responsePath: string;
  paginationType: DataMapperRestPaginationType;
  pageSize: string;
  maxPages: string;
  limitParam: string;
  offsetParam: string;
  pageParam: string;
  cursorParam: string;
  cursorPath: string;
}

/**
 * What a REST call carries that is never kept by a store: the auth secret, the
 * custom headers and the body. Held in the form's component state, cleared
 * after each call.
 */
export interface DataMapperRestSecrets {
  token: string;
  password: string;
  apiKeyValue: string;
  /** `key` is the form's row identity (React key), never sent. */
  headers: Array<{ key: number; name: string; value: string }>;
  body: string;
}

export const EMPTY_REST_SECRETS: DataMapperRestSecrets = {
  token: "",
  password: "",
  apiKeyValue: "",
  headers: [],
  body: "",
};

/** A saved source configuration. `config` never carries a secret (the API strips them). */
export interface DataMapperSourceConfig {
  id: string;
  name: string;
  type: string;
  config: Record<string, unknown>;
  scope: DataMapperScope;
  clientId: string | null;
  profileId: string | null;
  createdAt: string;
  updatedAt: string;
  droppedSecrets?: string[];
}

/** `data-mapper-data-source` `status` (and `connect`, plus `success`). */
export interface DataMapperSourceStatus {
  connected: boolean;
  type: string | null;
  name: string | null;
}

export interface DataMapperSourceColumn {
  name: string;
  dataType: string;
  sqlType: string;
  isNullable: boolean;
  maxLength?: number;
  precision?: number;
  scale?: number;
  isPrimaryKey: boolean;
  isForeignKey: boolean;
  foreignKeyTable?: string;
  foreignKeyColumn?: string;
  defaultValue?: string;
}

export interface DataMapperSourceTable {
  schema: string;
  name: string;
  fullName: string;
  rowCount?: number;
  columns: DataMapperSourceColumn[];
}

/**
 * One page of a data-plane answer. The API pages by BYTES (every answer stays
 * under 1 MB): `nextCursor` is the `$skip` for the next page, or null at the
 * end; `truncated` = fewer rows came back than were asked for, to fit.
 */
export interface DataMapperRowPage<T> {
  rows: T[];
  nextCursor: number | null;
  truncated: boolean;
}

/** `data-mapper-source-data` find. */
export interface DataMapperSourceDataPage extends DataMapperRowPage<Record<string, unknown>> {
  total: number;
  skip: number;
  limit: number;
}

/** The where operators `data-mapper-source-data` accepts, exactly. */
export type DataMapperFilterOperator =
  | "eq"
  | "neq"
  | "gt"
  | "lt"
  | "gte"
  | "lte"
  | "like"
  | "in"
  | "isNull"
  | "isNotNull";

export const DATA_MAPPER_FILTER_OPERATORS: readonly { value: DataMapperFilterOperator; label: string }[] = [
  { value: "eq", label: "=" },
  { value: "neq", label: "≠" },
  { value: "gt", label: ">" },
  { value: "lt", label: "<" },
  { value: "gte", label: "≥" },
  { value: "lte", label: "≤" },
  { value: "like", label: "like" },
  { value: "in", label: "in (a, b, …)" },
  { value: "isNull", label: "is empty" },
  { value: "isNotNull", label: "is not empty" },
];

export const operatorTakesValue = (op: DataMapperFilterOperator): boolean =>
  op !== "isNull" && op !== "isNotNull";

/** Shown for 'sql-host-not-allowed' (sql-host-policy.ts): the fix is an administrator's. */
export const SQL_HOST_NOT_ALLOWED_MESSAGE =
  "HQ is not allowed to connect to that SQL Server. An administrator must add the server " +
  "(host, or host:port for a port other than 1433) to DATA_MAPPER_ALLOWED_SQL_HOSTS on HQ's API " +
  "before anyone can connect to it.";

/** Shown for 'rest-host-not-allowed' (rest-host-policy.ts) when the host is not on the list. */
export const REST_HOST_NOT_ALLOWED_MESSAGE =
  "HQ is not allowed to call that API. An administrator must add its host (host, or host:port for a " +
  "port other than the scheme's default) to DATA_MAPPER_REST_ALLOWED_HOSTS on HQ's API before anyone " +
  "can connect to it.";

/**
 * Shown for 'rest-host-not-allowed' when the host IS listed but resolves to a
 * private address: listing it does not help, so the allowlist sentence would mislead.
 */
export const REST_HOST_PRIVATE_MESSAGE =
  "That API's host resolves to a private, loopback, link-local or metadata address. HQ never calls " +
  "those, even when the host is on DATA_MAPPER_REST_ALLOWED_HOSTS.";

/** The refusals that mean "this tab has no source open for this selection". */
export const NOT_CONNECTED_REASONS: ReadonlySet<string> = new Set(["not-connected", "selection-mismatch"]);

// ---- Target connections and imports ----------------------------------------
// Mirrors of data-mapper-target-connections.class.ts, data-mapper-imports.ts and
// data-plane/imports.ts (packages/api/src/services/data-mapper/).

/** A saved target SQL Server. Metadata only: the API has no password column and drops one if sent. */
export interface DataMapperTargetConnection {
  id: string;
  name: string;
  server: string;
  database: string;
  authType: DataMapperSqlAuthType;
  username: string | null;
  /** Windows auth's domain; null when none was saved. */
  domain?: string | null;
  port: number | null;
  encrypt: boolean | null;
  trustServerCertificate: boolean | null;
  hasPassword: boolean;
  scope: DataMapperScope;
  clientId: string | null;
  profileId: string | null;
  createdAt: string;
  updatedAt: string;
  droppedSecrets?: string[];
}

/** `data-mapper-target-connection` `status` (and `connect`, plus `success`). */
export interface DataMapperTargetStatus {
  connected: boolean;
  server: string | null;
  database: string | null;
}

/** One row of `list-tables` on the open target. */
export interface DataMapperTargetTable {
  schema: string;
  name: string;
  fullName: string;
  rowCount?: number;
}

export type DataMapperImportMode = "dry-run" | "write";
export type DataMapperWriteMode = "insert" | "truncate-insert" | "delete-insert";
export type DataMapperImportStatus = "running" | "completed" | "completed-with-errors" | "failed" | "cancelled";
export type DataMapperImportPhase = "preparing" | "clearing" | "reading" | "committing" | "done";

export const DATA_MAPPER_WRITE_MODES: readonly { mode: DataMapperWriteMode; label: string; destructive: boolean }[] = [
  { mode: "insert", label: "Insert (keep the existing rows)", destructive: false },
  { mode: "truncate-insert", label: "Truncate each table, then insert", destructive: true },
  { mode: "delete-insert", label: "Delete every row, then insert", destructive: true },
];

export const isDestructiveWriteMode = (mode: DataMapperWriteMode): boolean =>
  DATA_MAPPER_WRITE_MODES.find((m) => m.mode === mode)?.destructive === true;

export interface DataMapperImportRowError {
  sourceTable: string;
  /** 1-based position of the source row in the paging order. */
  row: number;
  field: string | null;
  message: string;
}

export interface DataMapperImportTableReport {
  sourceTable: string;
  targetTable: string;
  /** Rows read from the source. */
  read: number;
  valid: number;
  invalid: number;
  /** Rows inserted inside the transaction (kept only when `committed`). */
  written: number;
}

export interface DataMapperImportPreviewRow {
  sourceTable: string;
  row: number;
  valid: boolean;
  values: Record<string, unknown>;
}

export interface DataMapperImportReport {
  tables: DataMapperImportTableReport[];
  /** Problems with a target table found before any row was read. */
  targetIssues: Array<{ targetTable: string; message: string }>;
  errors: DataMapperImportRowError[];
  errorCount: number;
  errorsTruncated: boolean;
  preview: DataMapperImportPreviewRow[];
  previewTruncated: boolean;
  /**
   * Which cap cut the preview: `rows` = there were more rows than the preview
   * keeps, `bytes` = the rows kept reached its size limit first. Null: not cut.
   */
  previewCutBy?: "rows" | "bytes" | null;
  /** The preview's columns in the target table's order (the API's previewColumns). */
  previewColumns?: string[];
  committed: boolean;
  rolledBack: boolean;
}

/** `data-mapper-imports` get / cancel, and the final events' payload. */
export interface DataMapperImportView {
  importId: string;
  tabId: string;
  mode: DataMapperImportMode;
  writeMode: DataMapperWriteMode;
  status: DataMapperImportStatus;
  phase: DataMapperImportPhase;
  done: number;
  total: number;
  startedAt?: string;
  finishedAt?: string | null;
  error: { reason: string; message: string } | null;
  /** Present once the import has finished. */
  result: DataMapperImportReport | null;
}

/** What a finished import's event carries: the counts, never a row value (get() has the rest). */
export interface DataMapperImportSummary {
  tables: DataMapperImportTableReport[];
  errorCount: number;
  errorsTruncated: boolean;
  targetIssueCount: number;
  committed: boolean;
  rolledBack: boolean;
}

/**
 * The payload of `import-progress` / `import-complete` / `import-error`
 * (data-plane/imports.ts ImportEventPayload). `summary` on complete and error,
 * `error` on error only. Never the report itself: that is read with get().
 */
export interface DataMapperImportEventPayload {
  importId: string;
  tabId: string;
  status: DataMapperImportStatus;
  phase: DataMapperImportPhase;
  done: number;
  total: number;
  summary?: DataMapperImportSummary;
  error?: { reason: string; message: string } | null;
}

/**
 * The import service's realtime events, on service path `data-mapper-imports`.
 * Published only to the requesting user's sockets; every payload names its
 * `tabId`, and the web drops another tab's (tabEvents.ts).
 */
export const DATA_MAPPER_IMPORTS_PATH = "data-mapper-imports";
export const DATA_MAPPER_IMPORT_EVENTS = {
  progress: "import-progress",
  complete: "import-complete",
  error: "import-error",
} as const;
